/**
 * Settings (Настройки) — appearance and notification preferences.
 *
 * Tamagui port of the handoff `SettingsScreen` (screen-misc.jsx):
 *  • Appearance — dark-theme switch, interface language (live). The accent stays
 *    at the default navy (#1D4289); the picker UI is gone (accent is still wired
 *    through useApp + the theme system, see src/theme/accents).
 *  • Notifications — preference switches, shown read-only / locked
 *
 * Theme (dark/light), language and accent are wired to the real app context
 * (`useApp`) and persisted across reloads, so they take effect across the whole
 * portal. The notification switches are locked: the backend stores the prefs
 * but doesn't act on them yet (no push send / email channel, and notify()
 * ignores the per-type flags), so editing them would be misleading. The Privacy
 * section is hidden until the backend enforces showStatus / showBirthday.
 */
import { type ReactNode } from 'react'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { useApp, useUiPrefs, type Lang } from '../../src/i18n'
import { usePreferences } from '../../src/features/profile/useProfile'
import { useResponsive } from '../../src/lib/responsive'
import { Card, Enter } from '../../src/ui'
import { Page } from '../../src/ui/Screen'

const TERTIARY = '$tertiary'

/** Pill toggle — controlled. `disabled` locks it (non-interactive + dimmed). */
function Switch({
  checked,
  onChange,
  disabled = false,
}: {
  checked: boolean
  onChange?: (v: boolean) => void
  disabled?: boolean
}) {
  return (
    <XStack
      width={42}
      height={24}
      borderRadius={999}
      padding={2}
      opacity={disabled ? 0.5 : 1}
      cursor={disabled ? 'default' : 'pointer'}
      backgroundColor={checked ? '$primary' : '$borderColor'}
      onPress={disabled ? undefined : () => onChange?.(!checked)}
      accessibilityRole="switch"
      accessibilityState={{ checked, disabled }}
    >
      <Stack
        width={20}
        height={20}
        borderRadius={999}
        backgroundColor="#fff"
        x={checked ? 18 : 0}
        animation="quick"
        shadowColor="#000"
        shadowOpacity={0.2}
        shadowRadius={2}
        shadowOffset={{ width: 0, height: 1 }}
      />
    </XStack>
  )
}

/** Two-option segmented control (used for language). */
function Segmented({
  value,
  options,
  onChange,
}: {
  value: Lang
  options: { value: Lang; label: string }[]
  onChange: (v: Lang) => void
}) {
  return (
    <XStack
      borderWidth={1}
      borderColor="$borderColor"
      borderRadius={7}
      padding={2}
      backgroundColor="$background"
      flexWrap="wrap"
    >
      {options.map((o) => {
        const on = o.value === value
        return (
          <XStack
            key={o.value}
            cursor="pointer"
            animation="quicker"
            paddingHorizontal={14}
            paddingVertical={6}
            borderRadius={5}
            backgroundColor={on ? '$primary' : 'transparent'}
            hoverStyle={on ? undefined : { backgroundColor: '$backgroundHover' }}
            pressStyle={{ scale: 0.96 }}
            onPress={() => onChange(o.value)}
          >
            <Text fontSize={13} fontWeight="600" color={on ? '$onPrimary' : '$muted'}>
              {o.label}
            </Text>
          </XStack>
        )
      })}
    </XStack>
  )
}

function SettingRow({
  title,
  desc,
  control,
  stack = false,
}: {
  title: string
  desc?: string
  control: ReactNode
  /** Phone-only: stack label above control instead of a single row (wide controls, e.g. Segmented). */
  stack?: boolean
}) {
  return (
    <XStack
      gap={stack ? 8 : 16}
      paddingVertical={14}
      borderBottomWidth={1}
      borderColor="$borderSecondary"
      alignItems={stack ? 'flex-start' : 'center'}
      flexDirection={stack ? 'column' : 'row'}
    >
      <YStack flex={stack ? undefined : 1} width={stack ? '100%' : undefined}>
        <Text fontSize={13} fontWeight="600" color="$color">
          {title}
        </Text>
        {desc ? (
          <Text fontSize={11} color={TERTIARY} marginTop={2}>
            {desc}
          </Text>
        ) : null}
      </YStack>
      {control}
    </XStack>
  )
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card paddingHorizontal={20} paddingTop={4} paddingBottom={8}>
      <Text fontSize={13} fontWeight="700" color="$color" paddingTop={14} paddingBottom={4}>
        {title}
      </Text>
      {children}
    </Card>
  )
}

export default function SettingsScreen() {
  const { l, lang, setLang } = useApp()
  const { theme, setTheme, liveBg, setLiveBg } = useUiPrefs()
  const { isPhone } = useResponsive()

  // Read-only mirror of GET /users/me/preferences — the notification switches
  // below are locked (see file header). Appearance (theme/lang/accent) stays
  // client-persisted via useApp (deliberate).
  const { data: prefs } = usePreferences()
  const notif = prefs?.notifications

  return (
    <Page title={l('set.title')} subtitle={l('set.subtitle')} maxWidth={760}>
      {/* ── Appearance ─────────────────────────────────────────────── */}
      <Enter index={0}>
      <Block title={l('set.appearance')}>
        <SettingRow
          title={l('set.darkTheme')}
          desc={l('set.darkThemeDesc')}
          control={<Switch checked={theme === 'dark'} onChange={(on) => setTheme(on ? 'dark' : 'light')} />}
        />
        <SettingRow
          title={l('set.liveBg')}
          desc={l('set.liveBgDesc')}
          control={<Switch checked={liveBg} onChange={setLiveBg} />}
        />
        <SettingRow
          title={l('set.language')}
          stack={isPhone}
          control={
            <Segmented
              value={lang}
              onChange={setLang}
              options={[
                { value: 'ru', label: 'Русский' },
                { value: 'en', label: 'English' },
                { value: 'az', label: 'Azərbaycan' },
                { value: 'ka', label: 'ქართული' },
              ]}
            />
          }
        />
      </Block>
      </Enter>

      {/* ── Notifications ──────────────────────────────────────────────
          Locked: prefs persist but the backend doesn't act on them yet
          (no push send / email channel; notify() ignores per-type flags). */}
      <Enter index={1}>
      <Block title={l('set.notifications')}>
        <SettingRow title={l('set.notifMentions')} control={<Switch checked={!!notif?.mentions} disabled />} />
        <SettingRow title={l('set.notifNews')} control={<Switch checked={!!notif?.news} disabled />} />
        <SettingRow title={l('set.notifRequests')} control={<Switch checked={!!notif?.tasks} disabled />} />
        <SettingRow
          title={l('set.notifDigest')}
          desc={l('set.notifDigestDesc')}
          control={<Switch checked={!!notif?.channel.email} disabled />}
        />
      </Block>
      </Enter>

      {/* ── Privacy ────────────────────────────────────────────────────
          Hidden until the backend enforces showStatus / showBirthday. */}

      <Stack height={8} />
    </Page>
  )
}
